<template>
  <div
    v-block-loading:getTabs="loading"
    class="m-bet-assist"
    :class="{ 'm-bet-assist-insight': currentWidgetName === 'betInsights' }"
  >
    <template v-if="!loading">
      <template v-if="navList.length > 1">
        <VSnapNav
          v-if="!hideNav"
          v-model="currentNavTab"
          class="m-sub-navs"
          :style="{
            top: `${navFixedOffsetTop}px`
          }"
        >
          <template v-for="(item, index) in navList">
            <VSnapNavItem
              :key="index"
              class="m-sub-nav-item"
              :name="index"
              @click="handleNav(item)"
            >
              {{ item.label }}
              <Tag
                v-if="item.tagNew"
                class="widgets-nav-tag"
              >
                New
              </Tag>
            </VSnapNavItem>
          </template>
        </VSnapNav>

        <div class="m-widgets-container">
          <template v-for="(item, index) in navList">
            <div
              v-if="currentNavTab === index && item.children"
              :key="index"
              class="m-children-wrap"
            >
              <template v-for="(child, childIndex) in item.children">
                <div
                  :key="child.name"
                  class="section"
                >
                  <h4
                    v-if="!shouldHideSectionTitle(childIndex) && !shouldHideCustomWidget(child.name)"
                    class="section-title"
                  >
                    {{ child.label }}
                  </h4>

                  <template
                    v-if="child.component != null"
                  >
                    <component
                      :is="child.component"
                      v-if="!shouldHideCustomWidget(child.name)"
                      :event-id="eventId"
                      :event="event"
                      @hide-section-title="(shouldHide) => handleHideSectionTitle(childIndex, shouldHide)"
                    />
                  </template>

                  <Widgets
                    v-else
                    :name="child.name"
                    :event-id="eventId"
                    :sport-id="sportId"
                    :tabs-position="tabsPosition"
                  />
                </div>
              </template>
            </div>

            <template v-else>
              <BettingInsights
                v-if="currentNavTab === index && item.name === 'betInsights'"
                :key="index"
                :event="event"
              />
              <Widgets
                v-else-if="currentNavTab === index"
                :key="index"
                :name="item.name"
                :event-id="eventId"
                :sport-id="sportId"
                :tabs-position="tabsPosition"
              />
            </template>
          </template>
        </div>
      </template>

      <p
        v-else
        class="no-data"
      >
        No statistics available
      </p>
    </template>
  </div>
</template>

<script>
/**
 * 比赛 widgets
 */
import { ref, getCurrentInstance } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import { useWidgetsTabsStore } from '@ms/stores/detailWidgetsTabs.js'
import VSnapNav from 'fe-vui/snapNav'
import VSnapNavItem from 'fe-vui/snapNavItem'
import Widgets from 'base/components/Widgets'
import BettingInsights from '@ms/components/matchDetail/BettingInsights.vue'
import Tag from 'wap/components/Tag.vue'
import { getBetAssistWidgetNavs } from '@ms/components/widgets/widgetsNavs.js'

export default {
  name: 'BetAssist',
  components: {
    VSnapNav,
    VSnapNavItem,
    Widgets,
    BettingInsights,
    Tag
  },
  props: {
    event: {
      type: Object,
      default: () => ({})
    },
    eventId: {
      type: String,
      default: ''
    },
    sportId: {
      type: String,
      default: ''
    },
    roundType: {
      type: String,
      default: ''
    },
    list: {
      type: Array,
      default: () => null
    },
    // widget tab navs 位置，默认在下面，可传值 'top'
    tabsPosition: {
      type: String,
      default: ''
    },
    navFixedOffsetTop: {
      type: Number,
      default: 0
    },
    hideNav: {
      type: Boolean,
      default: false
    },
    nav: {
      type: String,
      default: () => null
    },
    hideBetInsights: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { proxy: vm } = getCurrentInstance()

    const { widgetsTabs } = storeToRefs(useWidgetsTabsStore())

    const loading = ref(false)

    const getTabs = async () => {
      try {
        loading.value = true
        const params = {
          eventId: props.event.eventId,
          searchType: 2
        }
        await useWidgetsTabsStore().getDetailWidgetsTabs(params)
        loading.value = false
        vm.handleTrace()
      } catch (error) {
        loading.value = false
        console.error('Failed to load tabs', error)
      }
    }
    getTabs()

    return { loading, widgetsTabs, getTabs }
  },
  data() {
    return {
      currentNavTab: 0,
      titleHiddenChildIndexList: []
    }
  },
  computed: {
    navList() {
      if (this.list) {
        // 比赛结束
        return this.list
      }
      const { eventId, sportId, roundType } = this
      if (!eventId || !sportId) {
        return []
      }
      let navs = getBetAssistWidgetNavs(sportId)
      if (!navs) return []
      if (roundType === 'cup') {
        navs.push({
          label: 'Cup Roster',
          name: 'cupRoster'
        })
      }

      if (sportId === 'sr:sport:1' && !this.hideBetInsights) {
        navs = [
          {
            label: 'Bet Insights',
            name: 'betInsights'
          },
          ...navs
        ]
      }

      if (this.widgetsTabs && this.widgetsTabs?.length) {
        const _navs = []
        this.widgetsTabs.forEach((x) => {
          navs.forEach((nav) => {
            if (nav.label === x.tabName) {
              _navs.push({ ...nav, tagNew: x.tagNew })
            }
          })
        })
        navs = _navs
      }

      return navs
    },
    currentWidgetName() {
      return this.navList[this.currentNavTab]?.name
    }
  },
  watch: {
    navList(val) {
      if (this.nav) {
        if (this.sportId === 'sr:sport:1') {
          const targetNavItemIndex = this.navList.findIndex((navItem) => navItem.name.includes(this.nav))
          this.currentNavTab = targetNavItemIndex
        }
        return
      }

      const userTab = localStorage.getItem('BET_ASSIST_TAB')

      if (userTab) {
        if (userTab !== 'betInsights') {
          const index = val.findIndex((x) => x.name === userTab)
          if (index !== -1) this.currentNavTab = index
          else this.currentNavTab = 0
        }
      }
    }
  },
  methods: {
    handleNav(item) {
      localStorage.setItem('BET_ASSIST_TAB', item.name)
      this.handleTrace()
    },
    handleHideSectionTitle(childIndex, shouldHide) {
      if (shouldHide) {
        if (this.titleHiddenChildIndexList.find((index) => index === childIndex)) return

        this.titleHiddenChildIndexList.push(childIndex)
        return
      }

      if (this.titleHiddenChildIndexList.findIndex((index) => index === childIndex)) {
        this.titleHiddenChildIndexList.splice(childIndex, 1)
        return
      }
    },
    shouldHideSectionTitle(childIndex) {
      return this.titleHiddenChildIndexList.find((index) => index === childIndex) !== undefined
    },
    shouldHideCustomWidget(componentName) {
      return this.hideBetInsights || (componentName === 'distributionOfGoals' && !this.widgetsTabs?.some((tab) => tab.tabName === 'Bet Insights'))
    },
    handleTrace() {
      const { navList, currentNavTab, event } = this
      const currentNav = navList[currentNavTab]
      if (currentNav) {
        const { label } = navList[currentNavTab]
        window.mstrace(`widgets_${label}`, '', { league_id: event.tournamentId })
      }
    }
  }
}
</script>

<style lang="less">
@import url("~wap/common/style/variable.less");

.m-bet-assist {
  .m-sub-navs {
    position: sticky;
    z-index: 10;
    width: 100%;
    background: #1b1b1b;

    .snap-nav {
      height: 44px;
      align-items: center;
    }

    .m-sub-nav-item {
      height: 24px;
      padding: 0 10px;
      border-radius: 12px;
      margin-right: 10px;
      background: #333;
      color: #999;
      font-size: 12px;
      line-height: 24px;

      &:first-child {
        margin-left: 13px;
      }

      &:last-child {
        margin-right: 15px;
      }

      &.active {
        background-image: linear-gradient(90deg, #ffca27 0%, #fed75f 100%);
        box-shadow: 0 2px 7px 0 rgba(255, 202, 39, 50%);
        color: #1b1b1b;
        font-weight: 700;
      }

      .widgets-nav-tag {
        top: -11px;
        right: -18px;
      }
    }
  }

  .section-title {
    position: relative;
    height: 45px;
    padding-left: 13px;
    background: #000;
    color: #ececec;
    font-size: 16px;
    line-height: 45px;
    text-align: justify;

    &::before {
      position: absolute;
      top: 50%;
      left: 0;
      width: 3px;
      height: 30px;
      background: #ffca27;
      content: "";
      transform: translateY(-50%);
    }
  }

  .no-data {
    padding: 20px 0;
    color: #fff;
    font-size: 12px;
    text-align: center;
  }

  &.m-bet-assist-insight {
    background: #212121;
  }
}

</style>
